import { useCallback, useEffect, useRef, useState } from "react"
import { fetchUgcVideos } from "../ugcExplorerApi"
import { POLL_INTERVAL_MS, MAX_POLL_ATTEMPTS } from "../constants"

// Mirrors the job_status states documented by the API:
//   processing -> no videos yet, keep polling
//   refreshing -> stored videos already in the response, render (polling optional)
//   completed  -> settled, stop
//   failed     -> stop, surface `error`
export const UGC_STATUS = {
  IDLE: "idle",             // no app selected yet
  LOADING: "loading",       // first request for this app/page, nothing to show yet
  PROCESSING: "processing", // job running, no videos yet (job_status: processing)
  READY: "ready",           // videos to render (job_status: completed or refreshing)
  FAILED: "failed",         // job_status: failed
  FORBIDDEN: "forbidden",   // HTTP 403 - tier gate, `error` holds the response body
  NOT_FOUND: "not_found",   // HTTP 404 - app doesn't exist
  ERROR: "error",           // any other error
}

/**
 * Loads a page of UGC videos for an app and, on first run, polls until the
 * backend's discovery job settles. Re-fires whenever appId/platform/page/sort
 * change; stale responses (a param changed mid-flight) are dropped.
 */
export default function useUgcVideos({ appId, platform, page = 1, pageSize = 9, sort = "views", filters = {} }) {
  const [data, setData] = useState(null)
  const [status, setStatus] = useState(UGC_STATUS.IDLE)
  const [error, setError] = useState(null)
  // True for exactly as long as a request is actually in flight - unlike
  // `status`, which deliberately stays READY (showing the previous page)
  // while a page/sort/filter change refetches on the same app+platform, so
  // the grid never blanks out on every click of "Next". Callers use this to
  // add a lighter-weight loading affordance (a button spinner, a dimmed
  // grid) on top of that stale-but-visible data instead of nothing at all.
  const [isFetching, setIsFetching] = useState(false)

  const pollTimeoutRef = useRef(null)
  const attemptsRef = useRef(0)
  const requestTokenRef = useRef(0)
  // Tracks which app+platform `data` actually belongs to. Page/sort/filter
  // changes within the *same* app+platform keep showing the previous page
  // while the next one loads (status stays READY - avoids a skeleton flash
  // on every click of "Next"/a sort option). But an app or platform switch
  // (e.g. the YouTube <-> Instagram tab) must not do that: without this
  // check, the previous platform's cards would keep rendering, fully
  // interactive, until the new fetch resolved - looking like the tab switch
  // did nothing. Only this case forces an immediate reset to LOADING with no
  // stale `data`.
  const loadedKeyRef = useRef(null)

  const stopPolling = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
  }, [])

  const load = useCallback(async () => {
    if (!appId) {
      stopPolling()
      setData(null)
      setStatus(UGC_STATUS.IDLE)
      setIsFetching(false)
      loadedKeyRef.current = null
      return
    }

    const requestToken = ++requestTokenRef.current
    stopPolling()
    setError(null)
    setIsFetching(true)

    const key = `${appId}::${platform}`
    const isNewAppOrPlatform = loadedKeyRef.current !== null && loadedKeyRef.current !== key
    loadedKeyRef.current = key

    if (isNewAppOrPlatform) {
      setData(null)
      setStatus(UGC_STATUS.LOADING)
    } else {
      setStatus(prev => (prev === UGC_STATUS.READY ? UGC_STATUS.READY : UGC_STATUS.LOADING))
    }

    try {
      const result = await fetchUgcVideos({ appId, platform, page, pageSize, sort, filters })
      if (requestToken !== requestTokenRef.current) return // superseded by a newer request

      if (result.job_status === "failed") {
        setData(null)
        setStatus(UGC_STATUS.FAILED)
        setError(result.error || "The video search failed. Please try again.")
        return
      }

      // `_appId`/`_platform` tag which app+platform this response answered, so
      // callers can tell fresh data from the previous platform's - right after
      // a tab switch, `data` still holds the old platform's page for a render.
      setData({ ...result, items: result.items ?? [], _appId: appId, _platform: platform })

      if (result.job_status === "processing") {
        attemptsRef.current += 1
        if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
          setStatus(UGC_STATUS.FAILED)
          setError("This is taking longer than expected. Please try again in a few minutes.")
          return
        }
        setStatus(UGC_STATUS.PROCESSING)
        pollTimeoutRef.current = setTimeout(load, POLL_INTERVAL_MS)
      } else {
        attemptsRef.current = 0
        setStatus(UGC_STATUS.READY)
      }
    } catch (err) {
      if (requestToken !== requestTokenRef.current) return

      if (err.status === 403) {
        setStatus(UGC_STATUS.FORBIDDEN)
        setError(err.data || {})
      } else if (err.status === 404) {
        setStatus(UGC_STATUS.NOT_FOUND)
        setError(err.message)
      } else {
        setStatus(UGC_STATUS.ERROR)
        setError(err.message || "Something went wrong.")
      }
    } finally {
      // Guard against a superseded (stale) request clearing the flag out
      // from under the newer request that replaced it - only the most
      // recent request gets to say the fetch is done.
      if (requestToken === requestTokenRef.current) setIsFetching(false)
    }
    // filters is compared by JSON below rather than listed here directly -
    // its identity changes every render in the caller, but its contents are
    // what should actually trigger a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, platform, page, pageSize, sort, stopPolling, JSON.stringify(filters)])

  useEffect(() => {
    attemptsRef.current = 0
    load()
    return stopPolling
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, platform, page, pageSize, sort, JSON.stringify(filters)])

  return { data, status, error, isFetching, reload: load }
}
